// client/src/components/TutorialManager.jsx
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaArrowRight, FaArrowLeft, FaCheck, FaTimes } from 'react-icons/fa';

const TutorialManager = ({ userId, onComplete, forceShow = false }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);

  // Get steps from translation
  const steps = t('tutorial.steps', { returnObjects: true }) || [];

  useEffect(() => {
    const hasSeen = localStorage.getItem(`tutorial_completed_${userId}`);
    if (forceShow) {
      setVisible(true);
      setStep(0);
    } else if (!hasSeen) {
      setVisible(true);
    } else {
      setVisible(false);
    }
  }, [userId, forceShow]);

  const scrollToTarget = (id) => {
    if (!id) return;
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.classList.add('ring-4', 'ring-yellow-400', 'shadow-xl', 'transition-all', 'duration-300');
      setTimeout(() => {
        element.classList.remove('ring-4', 'ring-yellow-400', 'shadow-xl');
      }, 2000);
    }
  };

  const nextStep = () => {
    const nextIndex = step + 1;
    if (nextIndex < steps.length) {
      setStep(nextIndex);
      setTimeout(() => {
        const targetId = steps[nextIndex].targetId;
        if (targetId) scrollToTarget(targetId);
      }, 100);
    } else {
      localStorage.setItem(`tutorial_completed_${userId}`, 'true');
      setVisible(false);
      if (onComplete) onComplete();
    }
  };

  const skipTutorial = () => {
    localStorage.setItem(`tutorial_completed_${userId}`, 'true');
    setVisible(false);
    if (onComplete) onComplete();
  };

  if (!visible || steps.length === 0) return null;

  const current = steps[step];
  const isLast = step === steps.length - 1;

  // أيقونة السهم حسب الاتجاه
  const ArrowIcon = isRTL ? FaArrowLeft : FaArrowRight;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-gray-900 rounded-2xl max-w-md w-full mx-4 border border-yellow-500/30 shadow-2xl overflow-hidden"
          dir={i18n.dir()} // لتحديد اتجاه النص
        >
          <div className="h-1 bg-gray-700">
            <div
              className="h-full bg-yellow-500 transition-all duration-300"
              style={{ width: `${((step + 1) / steps.length) * 100}%` }}
            />
          </div>
          <div className="p-6">
            <div className="flex justify-between items-center mb-4">
              <span className="text-xs text-gray-400">
                {t('tutorial.step')} {step + 1} {t('tutorial.of')} {steps.length}
              </span>
              <button onClick={skipTutorial} className="text-gray-400 hover:text-white transition">
                <FaTimes />
              </button>
            </div>
            <h3 className="text-2xl font-bold text-yellow-400 mb-3">{current.title}</h3>
            <p className="text-gray-300 leading-relaxed">{current.content}</p>
            <div className="flex gap-3 mt-6">
              {step > 0 && (
                <button
                  onClick={() => {
                    const prev = step - 1;
                    setStep(prev);
                    setTimeout(() => {
                      const targetId = steps[prev].targetId;
                      if (targetId) scrollToTarget(targetId);
                    }, 100);
                  }}
                  className="flex-1 bg-gray-700 hover:bg-gray-600 text-white py-2 rounded-lg transition flex items-center justify-center gap-2"
                >
                  {t('tutorial.back')}
                  {isRTL ? <FaArrowRight /> : <FaArrowLeft />}
                </button>
              )}
              <button
                onClick={nextStep}
                className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-black font-bold py-2 rounded-lg transition flex items-center justify-center gap-2"
              >
                {isLast ? (
                  <>{t('tutorial.finish')} <FaCheck /></>
                ) : (
                  <>{t('tutorial.next')} <ArrowIcon /></>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default TutorialManager;